
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ChangeDetectionStrategy, inject } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import { cloneDeep, first } from "lodash";
import { CheckboxInputComponent } from "rl-common/components/checkbox-input/checkbox-input.component";
import { DropdownSingleComponent } from "rl-common/components/dropdown/dropdown-single/dropdown-single.component";
import { DropdownOptions } from "rl-common/components/dropdown/dropdown.models";
import { FormTableControlDirective } from "rl-common/components/form-table/form-table-control.directive";
import { FormTableLabelDirective } from "rl-common/components/form-table/form-table-label.directive";
import { FormTableMessageDirective } from "rl-common/components/form-table/form-table-message.directive";
import { FormTableRowComponent } from "rl-common/components/form-table/form-table-row/form-table-row.component";
import { ComponentKey, sigmaReportCharTypes } from "rl-common/components/mod-details-layout/mod-layout.consts";
import { IPanelGroup, PanelGroupType } from "rl-common/components/mod-details-layout/mod-layout.models";
import { TextInputComponent } from "rl-common/components/text/text-input/text-input.component";
import { ComponentChanges } from "rl-common/models/i-component-change";
import { CharTypeId } from "rl-common/rl-common.consts";
import { FeatureKeys, FeatureService } from "company/components/features/feature.service";
import { LicensedFeaturesService } from "company/components/features/licensed-features/licensed-features.service";
import { Subscription } from "rxjs";


export interface IPanelForm {
	title: FormControl<string>;
	panelType: FormControl<KeyValueOption>;
	displayOnSummary: FormControl<boolean>;
}

interface KeyValueOption {
	id: string;
	value: string;
}

@Component({
	selector: "rl-edit-panel-modal",
	templateUrl: "./edit-panel-modal.component.html",
	styleUrls: ["./edit-panel-modal.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [FormTableRowComponent, FormTableLabelDirective, FormTableControlDirective, FormTableMessageDirective, TextInputComponent, ReactiveFormsModule, DropdownSingleComponent, CheckboxInputComponent]
})
export class EditPanelModalComponent implements OnInit, OnDestroy, OnChanges {
	private readonly _formBuilder = inject(FormBuilder);
	private readonly _featureService = inject(FeatureService);
	private readonly _licensedFeaturesService = inject(LicensedFeaturesService);


	@Input()
	index: number;

	@Input()
	panels: IPanelGroup[];

	@Input()
	templateCharType: number;

	otherPanels: IPanelGroup[];
	panel: IPanelGroup;
	form: FormGroup<IPanelForm>;

	@Output()
	onComplete = new EventEmitter<IPanelGroup>();

	_subs: Subscription[] = [];

	get isValid() {
		return this.form.valid;
	}

	get isNewPanel() {
		return this.index === null || this.index === undefined;
	}

	private readonly _soloSections = [ComponentKey.MultiAddress, ComponentKey.MultiEmailAddress, ComponentKey.SigmaElement];

	/**
	 * Whether this division can configure embedded Sigma reports. Resolved in ngOnInit because the licensed
	 * features service is observable only, while availablePanelTypeItems has to stay synchronous
	 * for buildForm(). The backing subjects replay, so this is set before the first read.
	 */
	canConfigureSigmaReports = false;

	panelTypeItems = [
		{ id: PanelGroupType.Configurable, value: "Configurable (Default)" },
		{ id: PanelGroupType.Addresses, value: "Addresses", availableOnCharTypes: [CharTypeId.User] },
		{ id: PanelGroupType.EmailAddresses, value: "Email Addresses", availableOnCharTypes: [CharTypeId.User], featureToggle: FeatureKeys.MultiEmailAddress },
		{ id: PanelGroupType.RecipientGroups, value: "Recipient Groups", availableOnCharTypes: [CharTypeId.User] },
		{ id: PanelGroupType.SigmaReport, value: "Report", availableOnCharTypes: sigmaReportCharTypes, requiresSigmaReportsLicense: true }
	];

	get availablePanelTypeItems(): KeyValueOption[] {
		return this.panelTypeItems.filter(item => {
			// whatever the panel is already saved as stays selectable. Otherwise a type that has since been
			// gated off would silently resolve to Configurable, and done() would strip the panel's
			// section -- deleting configuration we are only supposed to hide (RL-6630)
			if (item.id === this.panel?.type) {
				return true;
			}
			const matchesCharType = !item.availableOnCharTypes || item.availableOnCharTypes.includes(this.templateCharType);
			const featureEnabled = !item.featureToggle || this._featureService.isEnabled(item.featureToggle, this._featureService.features() ?? []);
			const licensed = !item.requiresSigmaReportsLicense || this.canConfigureSigmaReports;
			return matchesCharType && featureEnabled && licensed;
		}).map(item => {
 return { id: item.id, value: item.value } as KeyValueOption 
});
	}

	panelTypeOptions: DropdownOptions<KeyValueOption>;

	ngOnInit() {
		// resolved before the options and form are built below, so the Report type is filtered correctly
		const sub = this._licensedFeaturesService.canConfigureSigmaReports$().subscribe(canConfigure => {
			this.canConfigureSigmaReports = canConfigure;
		});
		this._subs.push(sub);

		this.buildForm();
	}

	ngOnChanges(changes: ComponentChanges<this>): void {
		if ((changes.panels && this.panels) || (changes.index && this.index >= 0)) {
			this.buildForm();
		}
	}

	buildForm() {
		this.otherPanels = cloneDeep(this.panels ?? []);
		if (!this.isNewPanel && this.index >= 0 && this.index < this.otherPanels.length) {
			this.panel = first(this.otherPanels.splice(this.index, 1));
		}
		// rebuilt alongside the form so the options and the selected value always agree
		const availableTypes = this.availablePanelTypeItems;
		this.panelTypeOptions = {
			items: availableTypes,
			rowKeyFn: (type: KeyValueOption) => type.id,
			rowLabelFn: (type: KeyValueOption) => type.value
		};
		const panelType = availableTypes.find(x => x.id === this.panel?.type) ?? first(availableTypes)
		this.form = this._formBuilder.group({
			title: new FormControl(this.panel?.title ?? ``, { validators: Validators.required }),
			displayOnSummary: new FormControl(this.panel?.displayOnSummary),
			panelType: new FormControl(panelType),
		}, { validators: this.panelValidator });
	}

	close() {
		this.onComplete.emit(null);
	}

	done() {
		const panel = this.panel ?? { title: ``, type: PanelGroupType.Configurable, displayOnSummary: false, sections: [], childGroups: [] };
		panel.title = this.form.controls.title.value;
		panel.displayOnSummary = this.form.controls.displayOnSummary.value;
		panel.type = this.form.controls.panelType.value.id as PanelGroupType;
		switch (panel.type) {
			case PanelGroupType.Addresses:
				panel.sections = [{ key: ComponentKey.MultiAddress }];
				break;
			case PanelGroupType.EmailAddresses:
				panel.sections = [{ key: ComponentKey.MultiEmailAddress }];
				break;
			case PanelGroupType.RecipientGroups:
				panel.sections = [{ key: ComponentKey.RecipientGroups }];
				break;
			case PanelGroupType.SigmaReport:
				// the report itself is chosen afterwards, by expanding the section in the layout
				// designer -- so preserve an existing selection rather than stamping over it
				if (!panel.sections?.some(x => x.key === ComponentKey.SigmaElement)) {
					panel.sections = [{ key: ComponentKey.SigmaElement }];
				}
				break;
			case PanelGroupType.Configurable:
				// if we switch to a configurable panel then we should remove any sections that are meant to be a dedicated panel
				panel.sections = panel.sections.filter(x => !this._soloSections.includes(x.key));
		}
		this.onComplete.emit(panel);
	}

	ngOnDestroy(): void {
		this._subs.forEach(x => x.unsubscribe());
	}

	private panelValidator: ValidatorFn = (form: FormGroup<IPanelForm>): ValidationErrors | null => {
		const panelTitle = form.controls.title.value.trim().toLocaleLowerCase();
		const otherTitles = this.otherPanels.map(x => x.title.trim().toLocaleLowerCase());
		if (otherTitles.includes(panelTitle)) {
			return { titleUniqueness: true };
		}
		return null;
	};

}